import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useOwnedShelters } from "@/hooks/useOwnedShelters";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Loader2, KeyRound, LogOut, Mail, AlertTriangle, Info, Trash2 } from "lucide-react";
import { toast } from "sonner";

const AccountSettings = () => {
  const { user, updatePassword, signOut } = useAuth();
  const { data: shelters } = useOwnedShelters(user?.id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const shelter = shelters?.[0];

  // Czy konto ma logowanie email/hasło? Konta założone tylko przez Google
  // nie mają hasła w naszej bazie - hasłem zarządza Google.
  const identities = (user?.identities ?? []) as Array<{ provider?: string }>;
  const hasEmailIdentity =
    identities.length === 0 // brak danych - traktujemy jak email/hasło, żeby nie blokować nadmiernie
      ? true
      : identities.some((i) => i.provider === "email");
  const socialProviders = identities
    .filter((i) => i.provider && i.provider !== "email")
    .map((i) => i.provider as string);
  const isGoogleOnly = !hasEmailIdentity && socialProviders.includes("google");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingPassword, setSavingPassword] = useState(false);
  const [resigning, setResigning] = useState(false);
  const [resignDialogOpen, setResignDialogOpen] = useState(false);
  const [nomineeEmail, setNomineeEmail] = useState("");
  const [nomineeNote, setNomineeNote] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isOwner = !!shelter;
  const canDeleteAccount = !isOwner;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Hasło musi mieć co najmniej 8 znaków");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Hasła nie są takie same");
      return;
    }
    setSavingPassword(true);
    try {
      await updatePassword(password);
      toast.success("Hasło zostało zmienione");
      setPassword("");
      setConfirmPassword("");
    } catch (err: any) {
      toast.error(err?.message ?? "Nie udało się zmienić hasła");
    } finally {
      setSavingPassword(false);
    }
  };

  const handleResign = async () => {
    if (!user || !shelter) return;
    setResigning(true);
    try {
      const { data, error } = await supabase.functions.invoke("resign-shelter-ownership", {
        body: {
          shelter_id: shelter.id,
          nominee_email: nomineeEmail.trim() || undefined,
          note: nomineeNote.trim() || undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const msg = nomineeEmail.trim()
        ? "Zrzeczono się dostępu. Propozycja następcy oczekuje na akceptację administratora."
        : "Zrzeczono się dostępu do schroniska.";
      toast.success(msg);
      setResignDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["owned-shelters"] });
      navigate("/panel", { replace: true });
    } catch (err: any) {
      toast.error(err?.message ?? "Nie udało się zrzec dostępu");
    } finally {
      setResigning(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation.trim() !== "USUWAM") {
      toast.error('Wpisz "USUWAM" aby potwierdzić');
      return;
    }
    setDeleting(true);
    try {
      const { data, error } = await supabase.functions.invoke("delete-account", {
        body: { confirmation: "USUWAM" },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success("Konto zostało usunięte");
      await signOut();
      queryClient.clear();
      navigate("/", { replace: true });
    } catch (err: any) {
      toast.error(err?.message ?? "Nie udało się usunąć konta");
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-2.5 sm:space-y-5 max-w-2xl">
      <div className="space-y-0.5">
        <h1 className="text-lg sm:text-2xl font-bold tracking-tight">Ustawienia konta</h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          Zarządzaj swoim kontem i dostępem do panelu
        </p>
      </div>

      {/* Adres e-mail (read-only) */}
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            Adres e-mail
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Twój adres logowania. Aby go zmienić, napisz na{" "}
            <a href="mailto:kontakt@gdzieschronisko.pl" className="underline hover:text-primary">
              kontakt@gdzieschronisko.pl
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
          <div className="rounded-md border bg-muted/30 px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium">
            {user?.email ?? "-"}
          </div>
        </CardContent>
      </Card>

      {/* Zmiana hasła - ukryte dla kont logujących się tylko przez Google */}
      <Card>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <KeyRound className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            {hasEmailIdentity ? "Zmień hasło" : "Hasło"}
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            {hasEmailIdentity
              ? "Ustaw nowe hasło - co najmniej 8 znaków"
              : isGoogleOnly
                ? "Logujesz się przez Google - hasło zarządzasz w swoim koncie Google."
                : "Logujesz się przez zewnętrznego dostawcę - hasło zarządzasz w jego ustawieniach."}
          </CardDescription>
        </CardHeader>
        {hasEmailIdentity ? (
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
            <form onSubmit={handleChangePassword} className="space-y-3 sm:space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="new-password" className="text-xs sm:text-sm">Nowe hasło</Label>
                <Input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-password" className="text-xs sm:text-sm">Powtórz nowe hasło</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  minLength={8}
                  required
                  className="h-9 sm:h-10 text-sm"
                />
              </div>
              <Button type="submit" disabled={savingPassword} size="sm" className="sm:h-10 sm:px-4">
                {savingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Zapisz nowe hasło
              </Button>
            </form>
          </CardContent>
        ) : (
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
            <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs sm:text-sm text-muted-foreground flex items-start gap-2">
              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <span>
                {isGoogleOnly
                  ? "Aby zmienić hasło, zrób to bezpośrednio w ustawieniach swojego konta Google."
                  : "Aby zmienić hasło, zrób to u swojego dostawcy logowania."}
              </span>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Rezygnacja z dostępu */}
      {shelter && (
        <Card data-danger-section>
          <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <LogOut className="h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
              Rezygnacja z dostępu
            </CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Operacja, której nie da się cofnąć samodzielnie
            </CardDescription>
          </CardHeader>
          <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
            <div className="rounded-lg border border-destructive/20 bg-destructive/[0.03] p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
              <div className="flex items-start gap-2.5 sm:gap-3 flex-1 min-w-0">
                <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-destructive shrink-0 mt-0.5" />
                <div className="space-y-1 min-w-0">
                  <h3 className="text-xs sm:text-sm font-semibold">
                    Zrzeknij się dostępu do {shelter.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Stracisz możliwość edycji profilu schroniska. Profil pozostanie
                    publicznie widoczny. Aby odzyskać dostęp, konieczne będzie ponowne
                    złożenie wniosku.
                  </p>
                </div>
              </div>

              <Dialog open={resignDialogOpen} onOpenChange={setResignDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    size="sm"
                    disabled={resigning}
                    className="h-8 px-3 bg-red-600/90 text-white hover:bg-red-600 shrink-0 self-start sm:self-center"
                  >
                    {resigning ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <LogOut className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    Zrzeknij się dostępu
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Zrzeknij się dostępu do {shelter.name}</DialogTitle>
                    <DialogDescription>
                      Po potwierdzeniu utracisz możliwość edycji profilu schroniska. Tej operacji nie da się cofnąć samodzielnie.
                    </DialogDescription>
                  </DialogHeader>

                  <div className="space-y-4 py-2">
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm">
                      <Info className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                      <p className="text-amber-900">
                        Jeśli nie wskażesz następcy, do czasu nadania dostępu nowej osobie dane schroniska będzie mógł aktualizować <strong>tylko administrator gdzieschronisko.pl</strong>.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="nominee-email" className="text-sm">
                        Email następcy <span className="text-muted-foreground font-normal">(opcjonalnie)</span>
                      </Label>
                      <Input
                        id="nominee-email"
                        type="email"
                        placeholder="np. nowy.opiekun@example.com"
                        value={nomineeEmail}
                        onChange={(e) => setNomineeEmail(e.target.value)}
                        maxLength={254}
                      />
                      <p className="text-xs text-muted-foreground">
                        Możesz wskazać osobę, której administrator nada dostęp po Twojej rezygnacji - może to być ktoś z Twojego zespołu lub osoba spoza systemu. Decyzję zatwierdza administrator gdzieschronisko.pl.
                      </p>
                    </div>

                    {nomineeEmail.trim() && (
                      <div className="space-y-2">
                        <Label htmlFor="nominee-note" className="text-sm">
                          Wiadomość dla następcy <span className="text-muted-foreground font-normal">(opcjonalnie)</span>
                        </Label>
                        <Textarea
                          id="nominee-note"
                          placeholder="Krótka informacja, którą zobaczy nowy opiekun w mailu"
                          value={nomineeNote}
                          onChange={(e) => setNomineeNote(e.target.value)}
                          maxLength={300}
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground text-right">
                          {nomineeNote.length}/300
                        </p>
                      </div>
                    )}
                  </div>

                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setResignDialogOpen(false)}
                      disabled={resigning}
                    >
                      Anuluj
                    </Button>
                    <Button
                      onClick={handleResign}
                      disabled={resigning}
                      className="bg-red-600/90 text-white hover:bg-red-600"
                    >
                      {resigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Tak, zrzekam się
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Usuń konto - RODO art. 17 */}
      <Card data-danger-section>
        <CardHeader className="p-3 sm:p-5 pb-2 sm:pb-3">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <Trash2 className="h-4 w-4 sm:h-5 sm:w-5 text-destructive" />
            Usuń konto
          </CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Trwałe usunięcie konta i powiązanych danych osobowych
          </CardDescription>
        </CardHeader>
        <CardContent className="p-3 sm:p-5 pt-0 sm:pt-0">
          <div className="rounded-lg border border-destructive/30 bg-destructive/[0.04] p-3 sm:p-4 flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
            <div className="flex items-start gap-2.5 sm:gap-3 flex-1 min-w-0">
              <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-destructive shrink-0 mt-0.5" />
              <div className="space-y-1 min-w-0">
                <h3 className="text-xs sm:text-sm font-semibold">
                  Trwałe usunięcie konta
                </h3>
                {isOwner ? (
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Jesteś głównym opiekunem schroniska <strong>{shelter?.name}</strong>.
                    Aby usunąć konto, musisz najpierw zrzec się dostępu do schroniska
                    (sekcja powyżej). Profil schroniska pozostanie publicznie widoczny.
                  </p>
                ) : (
                  <p className="text-xs sm:text-sm text-muted-foreground">
                    Twoje konto zostanie trwale usunięte. Tej operacji nie da się cofnąć.
                  </p>
                )}
              </div>
            </div>

            <Dialog open={deleteDialogOpen} onOpenChange={(o) => { setDeleteDialogOpen(o); if (!o) setDeleteConfirmation(""); }}>
              <DialogTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canDeleteAccount || deleting}
                  className="h-8 px-3 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive shrink-0 self-start sm:self-center"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  Usuń konto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Usunąć konto na zawsze?</DialogTitle>
                  <DialogDescription>
                    Po potwierdzeniu Twoje konto, dane logowania i powiązania osobiste
                    zostaną usunięte. Tej operacji nie da się cofnąć.
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-2">
                    <p className="text-amber-900 font-medium">Co zostanie zrobione:</p>
                    <ul className="text-amber-900 text-xs space-y-1 list-disc pl-4">
                      <li>Twoje konto i dane logowania - usunięte</li>
                      <li>Oczekujące wnioski o przejęcie - anulowane</li>
                      <li>Historia edycji w schroniskach - zanonimizowana (zachowana bez Twoich danych)</li>
                      <li>Profile schronisk - pozostają publicznie widoczne</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="delete-confirm" className="text-sm">
                      Aby potwierdzić, wpisz <strong>USUWAM</strong>
                    </Label>
                    <Input
                      id="delete-confirm"
                      value={deleteConfirmation}
                      onChange={(e) => setDeleteConfirmation(e.target.value)}
                      placeholder="USUWAM"
                      autoComplete="off"
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setDeleteDialogOpen(false)}
                    disabled={deleting}
                  >
                    Anuluj
                  </Button>
                  <Button
                    onClick={handleDeleteAccount}
                    disabled={deleting || deleteConfirmation.trim() !== "USUWAM"}
                    className="bg-red-600/90 text-white hover:bg-red-600"
                  >
                    {deleting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Usuń konto na zawsze
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountSettings;
